import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import ShareInviteSection from './ShareInviteSection';
import './share-modal.css';

/**
 * 노션 스타일 공유 팝오버.
 *  - "공유" 탭: 페이지별 권한(선택 공유) — ShareInviteSection
 *  - "웹에 게시" 탭: 외부 퍼블리시 토글 (기존 기능)
 * props: { pageId, onClose }
 * DB 함수:
 *   get_page_publish(p_page) -> { ok, published, token, allow_comment, search_indexable }
 *   set_page_publish(p_page, p_publish, p_allow_comment, p_indexable) -> { ok, token, published }
 */
export default function ShareModal({ pageId, onClose }) {
  const [tab, setTab] = useState('invite'); // 'invite' | 'publish'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const [published, setPublished] = useState(false);
  const [token, setToken] = useState(null);
  const [allowComment, setAllowComment] = useState(false);
  const [indexable, setIndexable] = useState(false);

  const copyTimer = useRef(null);

  // 마운트 시 현재 상태 로드
  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr('');
    (async () => {
      const { data, error } = await supabase.rpc('get_page_publish', { p_page: pageId });
      if (!active) return;
      if (error || !data || data.ok === false) {
        setErr('공유 설정을 불러오지 못했습니다.');
      } else {
        setPublished(!!data.published);
        setToken(data.token || null);
        setAllowComment(!!data.allow_comment);
        setIndexable(!!data.search_indexable);
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [pageId]);

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  // set_page_publish 호출 공용 헬퍼
  const apply = useCallback(async (next) => {
    setSaving(true);
    setErr('');
    const payload = {
      p_page: pageId,
      p_publish: next.publish,
      p_allow_comment: next.allowComment,
      p_indexable: next.indexable,
    };
    const { data, error } = await supabase.rpc('set_page_publish', payload);
    if (error || !data || data.ok === false) {
      setErr('변경 사항을 저장하지 못했습니다.');
      setSaving(false);
      return false;
    }
    setPublished(!!data.published);
    if (data.token) setToken(data.token);
    setSaving(false);
    return true;
  }, [pageId]);

  const onTogglePublish = useCallback(async () => {
    const nextPublish = !published;
    // 낙관적 토글 (실패 시 apply 가 published 를 서버값으로 되돌림)
    setPublished(nextPublish);
    await apply({ publish: nextPublish, allowComment, indexable });
  }, [published, allowComment, indexable, apply]);

  const onToggleComment = useCallback(async () => {
    const next = !allowComment;
    setAllowComment(next);
    await apply({ publish: published, allowComment: next, indexable });
  }, [allowComment, published, indexable, apply]);

  const onToggleIndexable = useCallback(async () => {
    const next = !indexable;
    setIndexable(next);
    await apply({ publish: published, allowComment, indexable: next });
  }, [indexable, published, allowComment, apply]);

  const publicUrl = token ? `${window.location.origin}/public/${token}` : '';

  const onCopy = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch (e) {
      // 클립보드 API 실패 시 폴백
      const ta = document.createElement('textarea');
      ta.value = publicUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e2) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1600);
  }, [publicUrl]);

  return (
    <>
      <div className="sm-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="sm-card" role="dialog" aria-label="공유">
        <div className="sm-head">
          <span className="sm-title">공유</span>
          <button type="button" className="sm-x" onClick={onClose} aria-label="닫기">✕</button>
        </div>

        <div className="sm-tabs" role="tablist" aria-label="공유 방식">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'invite'}
            className={`sm-tab${tab === 'invite' ? ' is-active' : ''}`}
            onClick={() => setTab('invite')}
          >
            공유
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'publish'}
            className={`sm-tab${tab === 'publish' ? ' is-active' : ''}`}
            onClick={() => setTab('publish')}
          >
            웹에 게시
          </button>
        </div>

        {tab === 'invite' ? (
          <div className="sm-body">
            <ShareInviteSection pageId={pageId} />
          </div>
        ) : loading ? (
          <div className="sm-loading">
            <div className="spinner" />
          </div>
        ) : (
          <div className="sm-body">
            {/* 웹에 게시 토글 */}
            <div className="sm-row">
              <div className="sm-row-text">
                <div className="sm-row-title">웹에 게시</div>
                <div className="sm-row-sub">링크가 있는 누구나 읽을 수 있습니다.</div>
              </div>
              <button
                type="button"
                className={`sm-switch${published ? ' is-on' : ''}`}
                onClick={onTogglePublish}
                disabled={saving}
                role="switch"
                aria-checked={published}
                aria-label="웹에 게시"
              >
                <span className="sm-knob" />
              </button>
            </div>

            {published && publicUrl ? (
              <div className="sm-link">
                <input className="sm-link-inp" value={publicUrl} readOnly aria-label="공개 링크" />
                <button type="button" className="sm-copy" onClick={onCopy}>
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
            ) : null}

            {published ? (
              <div className="sm-opts">
                <label className="sm-opt">
                  <input
                    type="checkbox"
                    checked={allowComment}
                    onChange={onToggleComment}
                    disabled={saving}
                  />
                  <span className="sm-opt-text">
                    <span className="sm-opt-title">댓글 허용</span>
                    <span className="sm-opt-sub">방문자가 댓글을 남길 수 있습니다.</span>
                  </span>
                </label>
                <label className="sm-opt">
                  <input
                    type="checkbox"
                    checked={indexable}
                    onChange={onToggleIndexable}
                    disabled={saving}
                  />
                  <span className="sm-opt-text">
                    <span className="sm-opt-title">검색엔진 노출</span>
                    <span className="sm-opt-sub">기본 꺼짐. 켜면 검색 결과에 노출될 수 있습니다.</span>
                  </span>
                </label>
              </div>
            ) : null}

            {err ? <div className="sm-err">{err}</div> : null}
          </div>
        )}
      </div>
    </>
  );
}
